import { CommonModule, DatePipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FileModel } from '@core/models/file.model';
import { MessageModel } from '@core/models/message.model';
import * as fromRoot from '@core/redux';
import * as MessageActions from '@core/redux/message/message.actions';
import { FileService } from '@core/services/file.service';
import { ImageService } from '@core/services/image/image.service';
import { getUserId } from '@core/utils/getUserId';
import { Store } from '@ngrx/store';
import { AvatarComponent } from '@ux/components/avatar/avatar.component';
import { ContextMenuItemComponent } from '@ux/components/context-menu/context-menu-item/context-menu-item.component';
import { ContextMenuComponent } from '@ux/components/context-menu/context-menu.component';
import { PreviewFileComponent } from '@ux/components/preview-file/preview-file.component';
import { ImagePreviewPipe } from '@ux/pipes/image-preview.pipe';
import * as _ from 'lodash';
import { NgClickOutsideDelayOutsideDirective } from 'ng-click-outside2';

interface CommentItemState {
  message: MessageModel;
  prevUserId: string | boolean;
  isShowMenu: boolean;
  deleteConfirm: number;
  images: any[][];
  files: any[];
}

@Component({
  selector: 'app-task-view-comment-item',
  templateUrl: './task-view-comment-item.component.html',
  styleUrls: ['./task-view-comment-item.component.less'],
  imports: [
    NgClass,
    DatePipe,
    CommonModule,
    AvatarComponent,
    ContextMenuComponent,
    ContextMenuItemComponent,
    PreviewFileComponent,
    ImagePreviewPipe,
    NgClickOutsideDelayOutsideDirective,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskViewCommentItemComponent implements OnInit {
  private readonly store = inject<Store<fromRoot.State>>(Store);
  private readonly fileService = inject(FileService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly imageService = inject(ImageService);
  readonly userId = getUserId();

  private readonly state = signal<CommentItemState>({
    message: new MessageModel(),
    prevUserId: false,
    isShowMenu: false,
    deleteConfirm: 0,
    images: [],
    files: [],
  });

  protected readonly currentMessage = computed(() => this.state().message);
  protected readonly previousUserId = computed(() => this.state().prevUserId);
  protected readonly isShowMenu = computed(() => this.state().isShowMenu);
  protected readonly deleteConfirm = computed(() => this.state().deleteConfirm);
  protected readonly images = computed(() => this.state().images);
  protected readonly files = computed(() => this.state().files);

  @Input()
  set message(value: MessageModel) {
    this.state.update(state => ({
      ...state,
      message: value,
    }));
    this.updateFiles();
  }

  @Input()
  set prevUserId(value: string | boolean) {
    this.state.update(state => ({
      ...state,
      prevUserId: value,
    }));
  }

  private updateFiles(): void {
    const messageFiles = this.state().message.files;
    const imageFiles = messageFiles.filter(file => file.isImage);
    const otherFiles = messageFiles.filter(file => !file.isImage);
    const chunkedImages = _.chunk(imageFiles, Math.ceil(imageFiles.length / 4));

    this.state.update(state => ({
      ...state,
      images: chunkedImages,
      files: otherFiles,
    }));
  }

  ngOnInit(): void {
    this.updateFiles();
  }

  onToggleMenu(): void {
    this.state.update(state => ({
      ...state,
      isShowMenu: !state.isShowMenu,
    }));
  }

  onCloseMenu(): void {
    this.state.update(state => ({
      ...state,
      isShowMenu: false,
    }));
  }

  onPin(): void {
    const newMessage = Object.assign(new MessageModel(), this.state().message, {
      isPin: !this.state().message.isPin,
    });
    this.store.dispatch(new MessageActions.PinAction(newMessage));
  }

  onLike(): void {
    const newMessage = Object.assign(new MessageModel(), this.state().message, {
      isLike: !this.state().message.isLike,
    });
    this.store.dispatch(new MessageActions.LikeAction(newMessage));
  }

  onDelete(): void {
    this.state.update(state => ({
      ...state,
      deleteConfirm: state.deleteConfirm + 1,
    }));

    if (this.state().deleteConfirm === 2) {
      this.store.dispatch(new MessageActions.DeleteAction(this.state().message));
    }
  }

  onEdit(): void {
    this.store.dispatch(new MessageActions.SetEditId(this.state().message.id));
  }

  onDeleteFile(file: FileModel): void {
    const currentMessage = this.currentMessage();
    if (!currentMessage.files) {
      const newMessage = Object.assign(new MessageModel(), currentMessage, {
        files: [],
      });
      this.state.update(state => ({
        ...state,
        message: newMessage,
      }));
      return;
    }

    const updatedFiles = currentMessage.files.filter(f => f.id !== file.id);
    const updatedMessage = new MessageModel().setAttributes({
      ...currentMessage,
      files: updatedFiles,
    });

    this.fileService
      .delete(file.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.state.update(state => ({
            ...state,
            message: updatedMessage,
          }));
          this.updateFiles();

          this.store.dispatch(new MessageActions.SaveSuccessAction(updatedMessage));

          this.cdr.markForCheck();
        },
        error: error => {
          console.error('Ошибка при удалении файла:', error);
        },
      });
  }
}
